import FullCalendar from "@fullcalendar/react";
import { Card, useTheme, Box, alpha, Tooltip, CircularProgress } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import itLocale from '@fullcalendar/core/locales/it'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'
import frLocale from '@fullcalendar/core/locales/fr'
import { useTranslation } from 'react-i18next';
import { EventContentArg, EventInput } from "@fullcalendar/core";
import {  getDecryptedNetDaily, fetchPaymentDataForRange } from "pages/employees/reportUtils";
import { getDecryptedSpecialDailyNet, decryptData } from "utils/encryptionDecryptionAgreements";
import { addDays, isAfter, isBefore, isSameDay, subMonths, addMonths } from "date-fns";
import { useEncryption } from "contexts/EncryptionKeyContext";
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { styled } from '@mui/material/styles';
import { calendarEntriesApi, agreementApi, employeePaymentsApi } from "api";
import { CalendarEntryEntityTypeEnum, RewardEntity } from "api/generated";
import EmployeeCalendarDayPopup from './EmployeeCalendarDayPopup';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface Props {
    date: Date;
    employeeId: number;
    seasonId: number | null;
    contractId: number;
    rewards: RewardEntity[];
    onDataChange?: () => void;
}


const StyledCalendarCard = styled(Card)(({ theme }) => ({
    height: 800,
    width: '100%',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease-in-out',
    background: theme.palette.background.paper,
    '&:hover': {
        boxShadow: `0 12px 48px ${alpha(theme.palette.common.black, 0.12)}`,
        transform: 'translateY(-2px)',
    },
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
    height: '100%',
    padding: theme.spacing(2),
    '--fc-bg-event-opacity': 0.8,
    '& .fc': {
        height: '100%',
        '& .fc-view-harness': {
            backgroundColor: 'transparent',
            borderRadius: theme.shape.borderRadius,
        },
        '& .fc-scrollgrid': {
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            '& .fc-scrollgrid-section > *': {
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            },
        },
        '& .fc-col-header': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
            '& .fc-col-header-cell': {
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                padding: theme.spacing(1),
                '& .fc-col-header-cell-cushion': {
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                },
            },
        },
        '& .fc-daygrid-body': {
            backgroundColor: 'transparent',
            '& .fc-daygrid-day': {
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    transform: 'scale(1.02)',
                    zIndex: 1,
                },
                '&.fc-day-today': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '& .fc-daygrid-day-number': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                },
                '& .fc-daygrid-day-frame': {
                    padding: '2px !important',
                },
            },
        },
        '& .fc-event': {
            borderRadius: theme.shape.borderRadius,
            border: 'none',
            padding: 0,
            margin: 0,
            minHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            backgroundColor: 'transparent !important',
            '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80%',
                height: '3px',
                borderRadius: '2px',
                opacity: 0.9,
            },
        },
    },
}));

const EmployeeCumulativeCalendar: FC<Props> = ({ date, employeeId, seasonId, contractId, rewards, onDataChange }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const [events, setEvents] = useState<EventInput[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const calendarRef = useRef<FullCalendar>(null);
    const { encryptionKey } = useEncryption();
    const [visibleRange, setVisibleRange] = useState({
        start: subMonths(date, 1),
        end: addMonths(date, 1)
    });
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const handleDayClick = (arg: any) => {
        setSelectedDate(arg.date);
        setPopupOpen(true);
    };

    const handleDataChange = async () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            const currentView = calendarApi.view;
            await fetchPayments(currentView.currentStart, currentView.currentEnd);
        }
    };

    // Calculate 730 balance for a specific date
    const get730BalanceForDate = (date: Date, agreements: any[]) => {
        // Find agreements with 730 balance that apply to this date
        const applicableAgreements = agreements
            .filter(agreement => {
                // Check if agreement has 730 balance
                const hasBalance = encryptionKey && encryptionKey !== ''
                    ? agreement.balance730_encrypted && agreement.balance730_encrypted !== ''
                    : agreement.balance730 !== null && agreement.balance730 !== 0;
                
                if (!hasBalance) return false;

                // Check if the agreement is active on this date
                const startDate = new Date(agreement.startedOn);
                const endDate = agreement.endedOn ? new Date(agreement.endedOn) : null;
                const isActiveOnDate = (isBefore(startDate, date) || isSameDay(startDate, date)) &&
                       (!endDate || isAfter(endDate, date) || isSameDay(endDate, date));

                if (!isActiveOnDate) return false;

                // Check if 730 balance date is on or before this date (carries forward)
                const balance730Date = agreement.balance730_date ? new Date(agreement.balance730_date) : null;
                return !balance730Date || balance730Date <= date;
            })
            .sort((a, b) => {
                const dateA = a.balance730_date ? new Date(a.balance730_date) : new Date(0);
                const dateB = b.balance730_date ? new Date(b.balance730_date) : new Date(0);
                return dateB.getTime() - dateA.getTime();
            });

        if (applicableAgreements.length === 0) {
            return 0;
        }

        // Get the most recent applicable agreement
        const applicableAgreement = applicableAgreements[0];

        // Get the balance value (decrypt if needed)
        return encryptionKey && encryptionKey !== '' && applicableAgreement.balance730_encrypted
            ? Number(decryptData(applicableAgreement.balance730_encrypted, encryptionKey))
            : applicableAgreement.balance730 || 0;
    };

    const fetchPayments = async (start: Date, end: Date) => {
        try {
            setIsLoading(true);
            const newEvents: EventInput[] = [];
            const agreements = (await agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined)).data;
            const actualEvents = (await calendarEntriesApi.findAll(employeeId.toString(), seasonId ?? 0)).data;
            const payments = (await employeePaymentsApi.findAll(employeeId, seasonId ?? 0)).data;

            const paymentData = await fetchPaymentDataForRange(
                employeeId,
                seasonId,
                contractId,
                encryptionKey,
                start,
                end
            );
            
            paymentData.forEach(data => {
                // Get 730 balance for this date
                const balance730 = get730BalanceForDate(data.date, agreements);
                
                const totalEarned = data.totalEarned +
                    rewards
                        .map((rw: RewardEntity) => {
                            return isAfter(data.date, new Date(rw.earnedOn)) ||
                                isSameDay(data.date, new Date(rw.earnedOn)) ?
                                Number(rw.amount) : 0
                        })
                        .reduce((a: number, b: number) => a + b, 0) + balance730;
                
                const balance = totalEarned - data.totalPaid;
                const isOverdue = data.totalPaid > totalEarned;

                // Check if there's a payment on this day
                const hasPayment = payments.some(payment => 
                    isSameDay(new Date(payment.paymentDate), data.date)
                );

                let dailyNet: number | undefined;
                let isSpecialDailyNet = false;
                let isEncrypted = false;

                // First check for special daily net across all agreements
                for (const agreement of agreements) {
                    if (agreement.SpecialDailyNet) {
                        const specialNet = agreement.SpecialDailyNet.find((sdn: { date: string; net: number; net_encrypted?: string }) => 
                            isSameDay(new Date(sdn.date), data.date)
                        );
                        if (specialNet) {
                            dailyNet = getDecryptedSpecialDailyNet(specialNet, encryptionKey);
                            isSpecialDailyNet = true;
                            isEncrypted = !!(specialNet.net_encrypted && encryptionKey && encryptionKey !== '');
                            break; // Found a special rate, no need to check other agreements
                        }
                    }
                }

                // If no special daily net found, find the applicable agreement for regular net daily
                if (!isSpecialDailyNet) {
                    const applicableAgreement = agreements.find(agreement => 
                        (isBefore(new Date(agreement.startedOn), data.date) || isSameDay(new Date(agreement.startedOn), data.date)) &&
                        (!agreement.endedOn || isAfter(new Date(agreement.endedOn), data.date) || isSameDay(new Date(agreement.endedOn), data.date))
                    );

                    if (applicableAgreement) {
                        const event = actualEvents.find((event) => 
                            (isBefore(new Date(event.startedOn), data.date) || isSameDay(new Date(event.startedOn), data.date)) &&
                            (isAfter(new Date(event.endedOn), data.date) || isSameDay(new Date(event.endedOn), data.date))
                        );
                        const hasUnpaidLeave = event?.type === CalendarEntryEntityTypeEnum.UnpaidLeave;
                        const netDaily = getDecryptedNetDaily(applicableAgreement, encryptionKey);
                        dailyNet = hasUnpaidLeave ? 0 : (netDaily ?? 0);
                    }
                }
                
                // Check if this is the exact date when 730 balance was applied
                const is730BalanceDate = agreements
                    .filter(agreement => {
                        const hasBalance = encryptionKey && encryptionKey !== ''
                            ? agreement.balance730_encrypted && agreement.balance730_encrypted !== ''
                            : agreement.balance730 !== null && agreement.balance730 !== 0;
                        
                        if (!hasBalance) return false;

                        const startDate = new Date(agreement.startedOn);
                        const endDate = agreement.endedOn ? new Date(agreement.endedOn) : null;
                        const isActiveOnDate = (isBefore(startDate, data.date) || isSameDay(startDate, data.date)) &&
                               (!endDate || isAfter(endDate, data.date) || isSameDay(endDate, data.date));

                        if (!isActiveOnDate) return false;

                        const balance730Date = agreement.balance730_date ? new Date(agreement.balance730_date) : null;
                        return !balance730Date || balance730Date <= data.date;
                    }).length > 0;

                newEvents.push({
                    id: data.date.toISOString(),
                    start: data.date,
                    end: addDays(data.date, 1),
                    allDay: true,
                    backgroundColor: 'transparent',
                    title: `${balance.toFixed(2)}€`,
                    classNames: ['custom-event'],
                    extendedProps: {
                        isOverdue,
                        balance,
                        dailyNet,
                        isSpecialDailyNet,
                        isEncrypted,
                        hasPayment,
                        balance730,
                        is730BalanceDate
                    }
                });
            });

            setEvents(newEvents);
        } catch (error) {
            console.error('Error fetching payments:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleDatesSet = (arg: any) => {
        const { start, end } = arg;
        setVisibleRange({
            start: subMonths(start, 1),
            end: addMonths(end, 1)
        });
        fetchPayments(start, end);
    };

    useEffect(() => {
        fetchPayments(visibleRange.start, visibleRange.end);
    }, [date, employeeId]);

    // Monitor encryption key changes and refresh calendar data
    useEffect(() => {
        if (encryptionKey && encryptionKey !== '') {
            // Encryption key is available, refresh calendar data
            if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                const currentView = calendarApi.view;
                fetchPayments(currentView.currentStart, currentView.currentEnd);
            }
        }
    }, [encryptionKey]);

    function findSelectedLanguage() {
        switch (i18n.language) {
            case 'en': return enLocale;
            case 'it': return itLocale;
            case 'de': return deLocale;
            case 'fr': return frLocale;
            default: return enLocale;
        }
    }

    function formatAmount(amount: number): string {
        // Remove trailing zeros and unnecessary decimal point
        const formatted = Number(amount.toFixed(2)).toString().replace('.', ',');
        return `${formatted}€`;
    }

    function renderEventContent(eventContent: EventContentArg) {
        const dailyNet = eventContent.event.extendedProps.dailyNet;
        const isSpecialDailyNet = eventContent.event.extendedProps.isSpecialDailyNet;
        const isEncrypted = eventContent.event.extendedProps.isEncrypted;
        const balance = eventContent.event.extendedProps.balance;
        const isOverdue = eventContent.event.extendedProps.isOverdue;
        const hasPayment = eventContent.event.extendedProps.hasPayment;
        const balance730 = eventContent.event.extendedProps.balance730;

        // Cumulative total color logic with gradient
        let amountBg = `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${alpha(theme.palette.success.main, 0.8)} 100%)`;
        let amountColor = theme.palette.success.contrastText;
        let amountIcon = <TrendingUpIcon sx={{ fontSize: 14 }} />;

        if (typeof balance === 'number' && balance < 0) {
            amountBg = `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${alpha(theme.palette.error.main, 0.8)} 100%)`;
            amountColor = theme.palette.error.contrastText;
            amountIcon = <TrendingDownIcon sx={{ fontSize: 14 }} />;
        }

        // Daily net color logic with modern styling
        let dailyNetBg = alpha(theme.palette.grey[200], 0.8);
        let dailyNetColor = theme.palette.text.primary;
        if (isSpecialDailyNet) {
            dailyNetBg = `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${alpha(theme.palette.warning.main, 0.8)} 100%)`;
            dailyNetColor = theme.palette.warning.contrastText;
        }

        return (
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    height: '100%',
                    width: '100%',
                    position: 'relative',
                    py: 0.5,
                    px: 0.5,
                }}
            >
                {hasPayment && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.success.main,
                            boxShadow: `0 0 4px ${alpha(theme.palette.success.main, 0.5)}`,
                        }}
                    />
                )}
                {balance730 !== 0 && eventContent.event.extendedProps.is730BalanceDate && (
                    <Tooltip 
                        title={balance730 > 0 ? t('employees.report.balance730.credit') : t('employees.report.balance730.debt')} 
                        placement="top"
                        arrow
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: balance730 > 0 ? theme.palette.warning.main : theme.palette.error.main,
                                boxShadow: `0 0 4px ${alpha(balance730 > 0 ? theme.palette.warning.main : theme.palette.error.main, 0.5)}`,
                                cursor: 'pointer',
                            }}
                        >
                            <AccountBalanceIcon 
                                sx={{ 
                                    fontSize: 10, 
                                    color: 'white',
                                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))'
                                }} 
                            />
                        </Box>
                    </Tooltip>
                )}
                {dailyNet !== undefined && (
                    <Tooltip 
                        title={isSpecialDailyNet ? t('employees.calendar.specialRate') : t('employees.calendar.standardRate')} 
                        placement="top"
                        arrow
                    >
                        <Box
                            sx={{
                                color: dailyNetColor,
                                background: dailyNetBg,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.25,
                                boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.08)}`,
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                backdropFilter: 'blur(4px)',
                                border: `1px solid ${alpha(dailyNetColor, 0.1)}`,
                                transition: 'all 0.2s ease-in-out',
                                maxWidth: '90%',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 3px 8px ${alpha(theme.palette.common.black, 0.12)}`,
                                }
                            }}
                        >
                            {isSpecialDailyNet && <StarIcon sx={{ fontSize: 12, color: theme.palette.warning.main }} />}
                            {isEncrypted && <LockIcon sx={{ fontSize: 10, color: theme.palette.primary.main, ml: 0.5 }} />}
                            {formatAmount(dailyNet)}
                        </Box>
                    </Tooltip>
                )}
                <Tooltip 
                    title={isOverdue ? t('employees.calendar.overdue') : t('employees.calendar.currentBalance')} 
                    placement="bottom"
                    arrow
                >
                    <Box 
                        className="amount-display"
                        sx={{
                            color: amountColor,
                            background: amountBg,
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.5,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                            position: 'relative',
                            zIndex: 2,
                            textAlign: 'left',
                            letterSpacing: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(amountColor, 0.1)}`,
                            transition: 'all 0.2s ease-in-out',
                            minWidth: 'auto',
                            maxWidth: '90%',
                            mt: 'auto',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
                            }
                        }}
                    >
                        {amountIcon}
                        {formatAmount(balance)}
                    </Box>
                </Tooltip>
            </Box>
        );
    }

    return (
        <StyledCalendarCard>
            <CalendarWrapper>
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.background.paper, 0.7),
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialDate={date}
                    events={events}
                    eventDisplay="background"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: ''
                    }}
                    firstDay={1}
                    locale={findSelectedLanguage()}
                    eventContent={renderEventContent}
                    ref={calendarRef}
                    dayHeaderFormat={{ weekday: 'narrow' }}
                    height="100%"
                    datesSet={handleDatesSet}
                    dateClick={handleDayClick}
                    buttonText={{
                        today: t('calendar.today'),
                        month: t('calendar.month'),
                        week: t('calendar.week'),
                        day: t('calendar.day')
                    }}
                />
                <EmployeeCalendarDayPopup
                    open={popupOpen}
                    onClose={() => {setPopupOpen(false)}}
                    date={selectedDate}
                    employeeId={employeeId}
                    seasonId={seasonId}
                    contractId={contractId}
                    rewards={rewards}
                    onDataChange={handleDataChange}
                />
            </CalendarWrapper>
        </StyledCalendarCard>
    );
}

export default EmployeeCumulativeCalendar;